import {
  ColumnDef,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  TableState,
  Updater,
} from '@tanstack/react-table';
import React, { useEffect, useRef } from 'react';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui/Stack';
import { GroupType } from 'src/WebApiController';

import { GroupHeaderRow } from './components/GroupHeaderRow';
import { Table as TableComponents } from './components/Table';
import { TablePagination } from './components/TablePagination';
import * as styles from './GroupedTable.css';
import { useTableSetup } from './hooks/useTableSetup';
import {
  RowWrapper,
  TablePaginationConfig,
  TableRowHeightVariant,
} from './types';

interface GroupedTableV2Props<T = any> {
  actionColId?: string;
  data: T[];
  columns: ColumnDef<T>[];
  variant?: TableRowHeightVariant;
  bordered?: boolean;
  alwaysShowCheckbox?: boolean;
  showSelectAllCheckbox?: boolean;
  getGroupId?: (data: T) => string | null | undefined; // Returns null/undefined for ungrouped rows
  getItemId?: (data: T) => string;
  getRowId?: (data: T, index: number) => string;
  isSelectionDisabled?: (data: T) => string | undefined;
  enableRowSelection?: boolean | ((row: Row<T>) => boolean);
  enableMultiRowSelection?: boolean | ((row: Row<T>) => boolean);
  enableSubRowSelection?: boolean | ((row: Row<T>) => boolean);
  onRowSelectionChange?: (updater: Updater<RowSelectionState>) => void;
  onExpandedChange?: (updater: any) => void; // For hierarchical data
  onPaginationChange?: (updater: Updater<PaginationState>) => void;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  onRowShiftSelect?: (
    tableRows: Row<T | null>[] | undefined,
    row: Row<T | null>,
    index: number
  ) => void;
  pagination?: TablePaginationConfig;
  className?: string;
  onRowClick?: (row: T, index: number) => void;
  summaryData?: T | null;
  style?: React.CSSProperties;
  state?: Partial<TableState>;
  sortingFns?: Record<string, any>;
  meta?: Record<string, any>;
  footerCta?: React.ReactNode;
  rowWrapper?: RowWrapper<T>;
  renderGroupHeader?: (groupId: string, groupData: T[]) => React.ReactNode;
  getSubRows?: (row: T) => T[]; // For hierarchical data
  isGroupRow?: (data: T) => boolean; // Identifies if a row is a group parent
}

export const GroupedSimpleTable = <T extends Record<string, any>>({
  actionColId,
  data,
  columns,
  variant = 'normal',
  alwaysShowCheckbox,
  getGroupId,
  getRowId,
  getItemId,
  isSelectionDisabled,
  onRowSelectionChange,
  onExpandedChange,
  onPaginationChange,
  onSortingChange,
  enableRowSelection,
  enableMultiRowSelection,
  enableSubRowSelection,
  onRowShiftSelect,
  pagination,
  className,
  onRowClick,
  summaryData,
  state,
  sortingFns,
  meta,
  footerCta,
  rowWrapper,
  renderGroupHeader,
  getSubRows,
  isGroupRow,
}: GroupedTableV2Props<T>) => {
  // Parent (ListingTableV2) manages expanded state - just use it
  const {
    table,
    tableColumns,
    renderHeaderGroups,
    renderRowContent,
    showCheckbox,
  } = useTableSetup({
    actionColId,
    data, // Pass original hierarchical data
    columns,
    alwaysShowCheckbox,
    getGroupId,
    getItemId,
    isSelectionDisabled,
    onRowSelectionChange,
    onExpandedChange,
    onPaginationChange,
    onSortingChange,
    enableRowSelection,
    enableMultiRowSelection,
    enableSubRowSelection,
    pagination,
    summaryData,
    state, // Use state from parent (includes expanded)
    sortingFns,
    meta,
    getSubRows, // Pass to TanStack for hierarchy handling
    isGroupRow, // Pass to determine which rows can expand
    getRowId,
    getRowCanExpand: isGroupRow
      ? (row) => Boolean(isGroupRow(row.original))
      : undefined,
  });

  // Measure container width with ResizeObserver and set as CSS variable
  const containerRef = useRef<HTMLDivElement>(null);
  const TableContainerWidth = '--table-container-width';

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        for (const entry of entries) {
          const width = Math.round(entry.contentRect.width);
          element.style.setProperty(TableContainerWidth, `${width}px`);
        }
      });
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [TableContainerWidth]);

  // Get all rows from TanStack
  const tableRows = table.getRowModel().rows;

  const handleClick = (
    row: Row<T>,
    e: React.MouseEvent,
    visualIndex: number
  ) => {
    e.stopPropagation();
    onRowShiftSelect?.(
      visualRows as Row<T | null>[],
      row as Row<T | null>,
      visualIndex
    );
  };
  // Memoize the grouping logic to avoid recalculating on every render
  const groupedTableRows = React.useMemo(() => {
    const ungroupedRows: typeof tableRows = [];
    const groupsMap = new Map<
      string,
      { parent: (typeof tableRows)[0]; children: typeof tableRows }
    >();

    // Build row lookup map for O(1) access instead of O(n) find
    const rowsById = new Map<string, (typeof tableRows)[0]>();
    tableRows.forEach((row) => {
      rowsById.set(row.id, row);
    });

    // Process rows
    tableRows.forEach((row) => {
      if (row.depth === 0) {
        if (isGroupRow?.(row.original)) {
          // Top-level parent group
          groupsMap.set(row.id, { parent: row, children: [] });
        } else {
          // Ungrouped row
          ungroupedRows.push(row);
        }
      } else if (row.depth > 0 && row.parentId) {
        // Child row - walk up to find top-level parent
        let current = row;
        let topLevelParentId: string | null = null;

        while (current.depth > 0 && current.parentId) {
          const parent = rowsById.get(current.parentId);
          if (!parent) break;

          if (parent.depth === 0) {
            topLevelParentId = parent.id;
            break;
          }
          current = parent;
        }

        // Add to parent's children if found
        if (topLevelParentId) {
          // Skip Active listing groups that are direct children (depth 1) of top-level parent
          const listing = (row.original as any)?.listing;
          const isActiveGroupAtDepth1 =
            row.depth === 1 &&
            listing?.isLtGrp &&
            listing?.groupType === GroupType.Active;

          if (!isActiveGroupAtDepth1) {
            groupsMap.get(topLevelParentId)?.children.push(row);
          }
        }
      }
    });

    return {
      ungrouped: ungroupedRows,
      groupedRowsByParent: Array.from(groupsMap.values()),
    };
  }, [tableRows, isGroupRow]);

  const { ungrouped, groupedRowsByParent } = groupedTableRows;

  // Create flat array of rows in visual order for shift-select
  const visualRows = React.useMemo(() => {
    const rows: typeof tableRows = [...ungrouped];
    groupedRowsByParent.forEach((group) => {
      rows.push(...group.children);
    });
    return rows;
  }, [ungrouped, groupedRowsByParent]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <TableComponents.Root
        table={table}
        variant={variant}
        className={className}
      >
        <TableComponents.Header>
          <TableComponents.Row>{renderHeaderGroups()}</TableComponents.Row>
        </TableComponents.Header>

        {/* Ungrouped rows in their own tbody */}
        {ungrouped.length > 0 && (
          <TableComponents.Body>
            {ungrouped.map((row, index) => {
              const cellContent = renderRowContent(row);
              const wrappedContent = rowWrapper
                ? rowWrapper(row.original, cellContent, index)
                : cellContent;

              return (
                <TableComponents.Row
                  key={row.id}
                  selected={row.getIsSelected()}
                  onClick={(e: React.MouseEvent) => handleClick(row, e, index)}
                >
                  {wrappedContent}
                </TableComponents.Row>
              );
            })}
          </TableComponents.Body>
        )}

        {/* Each group gets its own tbody with visual separation */}
        {groupedRowsByParent.map((group, groupIndex) => {
          const groupId = getItemId?.(group.parent.original) ?? group.parent.id;
          const childData = group.children.map((r: Row<T>) => r.original);
          const isExpanded = group.parent.getIsExpanded();
          const toggleExpanded = group.parent.toggleExpanded;
          if (!groupId) return null;

          // Calculate base visual index for this group's children
          const baseIndexForGroup =
            ungrouped.length +
            groupedRowsByParent
              .slice(0, groupIndex)
              .reduce((sum, g) => sum + g.children.length, 0);

          // Group header visual index is at the start of its children
          const groupHeaderVisualIndex = baseIndexForGroup;

          // Grouped Header Row
          return (
            <React.Fragment key={groupId}>
              <tbody className={styles.groupTbody}>
                <TableComponents.Row
                  selectable={Boolean(onRowShiftSelect)}
                  selected={group.parent.getIsSelected()}
                  onClick={(e: React.MouseEvent) =>
                    handleClick(group.parent, e, groupHeaderVisualIndex)
                  }
                >
                  <GroupHeaderRow
                    groupId={groupId}
                    childData={childData}
                    isExpanded={isExpanded}
                    toggleExpanded={() => toggleExpanded(!isExpanded)}
                    renderGroupHeader={renderGroupHeader}
                    showCheckbox={showCheckbox}
                    parentRow={group.parent}
                    table={table}
                    getGroupId={getGroupId}
                    selected={group.parent.getIsSelected()}
                    getItemId={getItemId}
                    isSelectionDisabled={isSelectionDisabled}
                    tableContainerWidth={TableContainerWidth}
                  />
                </TableComponents.Row>

                {/* Group child rows */}
                {group.children.map((row: Row<T>, index: number) => {
                  const visualIndex = baseIndexForGroup + index;
                  const cellContent = renderRowContent(row);
                  const wrappedContent = rowWrapper
                    ? rowWrapper(row.original, cellContent, visualIndex)
                    : cellContent;

                  const isListingGroup = (row.original as any)?.isLtGrp;

                  return (
                    <TableComponents.Row
                      key={row.id}
                      className={
                        isListingGroup ? styles.listingGroupRow : undefined
                      }
                      selected={row.getIsSelected()}
                      onClick={(e: React.MouseEvent) =>
                        handleClick(row, e, visualIndex)
                      }
                    >
                      {wrappedContent}
                    </TableComponents.Row>
                  );
                })}
              </tbody>
            </React.Fragment>
          );
        })}

        <TableComponents.Footer />
      </TableComponents.Root>

      <Stack
        justifyContent="spaceBetween"
        style={{ marginTop: vars.spacing.m }}
      >
        {pagination && (
          <TablePagination
            table={table}
            pageSize={pagination.pageSize}
            variant={pagination.variant}
          />
        )}
        {footerCta}
      </Stack>
    </div>
  );
};
